import { CaseStatus } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import { emailLinkMarkdown } from '@platform-ui-common/components/Link/EmailLink.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import {
  ApplicationSummaryTable,
  FullWidthPanel,
  StatusBlock,
} from '@platform-ui/application/components/index.js';
import { StatusBlockSection } from '@platform-ui/application/hooks/useApplicationStatusBlocks/getStatusBlocks.js';
import useApplicationStatusBlocks from '@platform-ui/application/hooks/useApplicationStatusBlocks/index.js';
import useUserApplications from '@platform-ui/application/hooks/useUserApplications.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import { IdentityPaths } from '@platform-ui/auth/Routes.js';
import UserDataModal from '@platform-ui/auth/components/UserDataModal/index.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { Box, Flex, Heading, Separator, Text } from '@radix-ui/themes';
import { IKImage } from 'imagekitio-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

export default function ApplicationSummaryPortal(): JSX.Element {
  const partner = usePartner();
  const { whitelabeling } = partner;
  const {
    loading,
    user,
    hasFullProfile,
    identity: { refreshUser },
  } = useAuth();
  const [userDataModalOpen, setUserDataModalOpen] = useState(false);
  const applications = useUserApplications();
  const navigate = useNavigate();

  const isAnonymousUser = user?.core?.isAnonymous ?? false;

  useEffect(() => {
    if (!hasFullProfile) {
      setUserDataModalOpen(true);
    } else {
      setUserDataModalOpen(false);
    }
  }, [hasFullProfile]);

  const showArchivedWarning = !!applications.filter(
    (app) => app.case.status === CaseStatus.Archived,
  ).length;

  const openApplications = applications.filter((app) => app.case.status !== CaseStatus.Archived);

  const statusBlocks = useApplicationStatusBlocks();
  if (showArchivedWarning)
    statusBlocks[StatusBlockSection.SubmittedPrograms].push({
      id: 'Archived',
      label: 'Archived Applications Warning',
      status: 'Unknown',
      message: `If you do not see an application you submitted below, it may have been archived.
Applications are most often marked archived because you have an existing open application. 
If you believe a mistake has occurred please reach out to us at ${emailLinkMarkdown(
        partner.email,
      )}.`,
    });

  useEffect(() => {
    if (!loading && (!user.gcip || !user.core)) navigate(IdentityPaths.SIGNIN);
  }, [loading, user, navigate]);

  return (
    <FullWidthPanel>
      <div className="flex flex-col text-center">
        <Box mb="4" mx="auto">
          <IKImage
            src={whitelabeling.logo}
            alt={`${partner.name} logo`}
            className="max-h-[224px] max-w-[224px]"
            urlEndpoint="https://ik.imagekit.io/bybeam/"
            transformation={[
              { height: '224', width: '224', c: 'at_max', dpr: `${window.devicePixelRatio}` },
            ]}
          />
        </Box>
        <Heading size="7" mb="4">
          {partner.whitelabeling.platformName}
        </Heading>
        <Flex direction="column" mb="5" gap="2">
          {isAnonymousUser ? (
            <>
              <Heading weight="regular" size="3">
                You're signed in as a guest.
              </Heading>
              <Text size="2">
                Your application progress is saved on this device only. Link your account to keep
                access across devices and receive updates.
              </Text>
              <div className="flex justify-center">
                <Button variant="text" link={ApplicantRoutes.PROFILE}>
                  Go to Profile to link your account
                </Button>
              </div>
            </>
          ) : (
            <>
              <Heading weight="regular" size="3">
                Welcome, <b>{user?.core?.name}</b>.
              </Heading>
              <Text size="2">Here you can find your current and past applications.</Text>
            </>
          )}
        </Flex>
        <Separator decorative size="4" mb="2" />
        <div className="flex flex-col gap-y-6">
          {Object.values(StatusBlockSection).map((section) => {
            if (statusBlocks[section].length === 0) return null;
            return (
              <Flex key={section} direction="column" gap="2" align="start">
                <Box position="sticky">
                  <Heading weight="medium" size="4">
                    {section}
                  </Heading>
                </Box>
                {statusBlocks[section].map((block) => (
                  <StatusBlock key={block.id} {...block} isAnonymousUser={isAnonymousUser} />
                ))}
              </Flex>
            );
          })}
          <Separator decorative size="4" mb="2" />
          {!!openApplications.length && <ApplicationSummaryTable applications={openApplications} />}
        </div>
      </div>
      {userDataModalOpen && <UserDataModal user={user.core} onSubmit={refreshUser} />}
    </FullWidthPanel>
  );
}
